export type EstoqueStatus =
  | "avaliado"
  | "comprado"
  | "em_preparacao"
  | "anunciado"
  | "reservado"
  | "vendido"
  | "consignado_ativo"
  | "devolvido"

export type LojaAvaliacaoResumo = {
  id: string
  status: string
  objetivo_comercial?: string | null
}

export type LojaPropostaResumo = {
  id: string
  modalidade?: string | null
  status?: string | null
  share_token?: string | null
  cliente_assinado_em?: string | null
  valor_fipe?: number | null
  custo_reparos?: number | null
  valor_ofertado?: number | null
  margem_lucro?: number | null
}

export type LojaComercialStatus =
  | "veiculo_cadastrado"
  | "avaliacao_iniciada"
  | "avaliacao_concluida"
  | "proposta_preparada"
  | "proposta_enviada"
  | "proposta_aceita"
  | "consignado_ativo"
  | "comprado"
  | "vendido"
  | "devolvido"

export type LojaComercialContexto = {
  status: LojaComercialStatus
  label: string
  proximaAcao: string
  helper: string
  tipo: "compra" | "consignacao"
}

export const getLojaComercialStatus = (
  avaliacao?: LojaAvaliacaoResumo | null,
  proposta?: LojaPropostaResumo | null,
  estoqueStatus?: string | null
): LojaComercialContexto => {
  const tipo = proposta?.modalidade === "consignacao" || avaliacao?.objetivo_comercial === "consignacao" ? "consignacao" : "compra"

  if (estoqueStatus === "vendido") {
    return { status: "vendido", label: "Vendido", proximaAcao: "Ver histórico", helper: "Veículo já foi marcado como vendido.", tipo }
  }
  if (estoqueStatus === "devolvido") {
    return { status: "devolvido", label: "Devolvido", proximaAcao: "Ver histórico", helper: "Consignação encerrada com devolução.", tipo }
  }
  if (estoqueStatus === "comprado") {
    return { status: "comprado", label: "Comprado", proximaAcao: "Preparar estoque", helper: "Veículo comprado e pronto para controle de estoque.", tipo: "compra" }
  }
  if (estoqueStatus === "consignado_ativo") {
    return { status: "consignado_ativo", label: "Consignado ativo", proximaAcao: "Acompanhar prazo", helper: "Termo assinado e veículo ativo em consignação.", tipo: "consignacao" }
  }

  if (!avaliacao) {
    return { status: "veiculo_cadastrado", label: "Veículo cadastrado", proximaAcao: "Iniciar avaliação", helper: "Cadastre a avaliação para decidir compra ou consignação.", tipo }
  }

  if (avaliacao.status !== "concluida") {
    return { status: "avaliacao_iniciada", label: "Avaliação iniciada", proximaAcao: "Continuar avaliação", helper: "Complete a avaliação para calcular reparos, risco e proposta.", tipo }
  }

  if (!proposta) {
    return {
      status: "avaliacao_concluida",
      label: "Avaliação concluída",
      proximaAcao: "Criar proposta",
      helper: "A avaliação já pode virar proposta de compra ou termo de consignação.",
      tipo,
    }
  }

  if (proposta.cliente_assinado_em || proposta.status === "aceita" || proposta.status === "assinada") {
    return tipo === "consignacao"
      ? { status: "proposta_aceita", label: "Proprietário assinou", proximaAcao: "Ativar consignado", helper: "Termo de consignação assinado pelo proprietário.", tipo }
      : { status: "proposta_aceita", label: "Proposta aceita", proximaAcao: "Marcar como comprado", helper: "Proposta assinada pelo cliente/proprietário.", tipo }
  }

  if (proposta.share_token || proposta.status === "enviada") {
    return tipo === "consignacao"
      ? { status: "proposta_enviada", label: "Termo enviado", proximaAcao: "Aguardar assinatura", helper: "Termo de consignação compartilhado com o proprietário.", tipo }
      : { status: "proposta_enviada", label: "Proposta enviada", proximaAcao: "Aguardar aceite", helper: "Proposta de compra compartilhada com o cliente.", tipo }
  }

  return tipo === "consignacao"
    ? { status: "proposta_preparada", label: "Termo preparado", proximaAcao: "Enviar termo", helper: "Revise valor anunciado, comissão e prazo antes de enviar.", tipo }
    : { status: "proposta_preparada", label: "Proposta preparada", proximaAcao: "Enviar proposta", helper: "Revise FIPE, reparos e oferta antes de enviar.", tipo }
}

export const getRiscoCompra = (valorFipe?: number | null, custoReparos?: number | null) => {
  if (!valorFipe || valorFipe <= 0) return { label: "Sem FIPE", className: "bg-muted text-muted-foreground" }
  const percentual = ((custoReparos ?? 0) / valorFipe) * 100
  if (percentual >= 12) return { label: "Risco alto", className: "bg-red-50 text-red-700 border-red-200" }
  if (percentual >= 6) return { label: "Risco médio", className: "bg-amber-50 text-amber-700 border-amber-200" }
  return { label: "Risco baixo", className: "bg-green-50 text-green-700 border-green-200" }
}

export const ESTOQUE_STATUS_LABELS: Record<EstoqueStatus, string> = {
  avaliado: "Avaliado",
  comprado: "Comprado",
  em_preparacao: "Em preparação",
  anunciado: "Anunciado",
  reservado: "Reservado",
  vendido: "Vendido",
  consignado_ativo: "Consignado ativo",
  devolvido: "Devolvido",
}
